import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useMetalRates } from '../context/MetalRatesContext';
import { calculatePrice } from '../utils/priceCalculator';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';
import { subscribeToStockChanges } from '../utils/stockSync';
import { ProductsSEO } from '../components/SEOHead';

// Model names for filtering
const MODEL_NAMES = [
  'Mangalsutra', 'Cocktail', 'Box Chain', 'Rope Chain', 'Cable Chain', 'Figaro', 'Curb Chain',
  'Traditional', 'Modern', 'Kada', 'Cuff', 'Tennis', 'Charm', 'Link', 'Bangle Style',
  'Solitaire', 'Eternity', 'Band', 'Halo', 'Engagement', 'Choker', 'Princess', 'Matinee',
  'Opera', 'Rope', 'Collar', 'Stud', 'Drop', 'Hoop', 'Jhumka', 'Chandbali', 'Danglers',
  'Heart', 'Cross', 'Religious', 'Initial', 'Ring', 'Diamond', 'Short', 'Long'
];

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const { metalRates } = useMetalRates();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedMetal, setSelectedMetal] = useState('all');
  const [selectedCarat, setSelectedCarat] = useState('all');
  const [selectedModelCategory, setSelectedModelCategory] = useState(searchParams.get('model_category') || 'all');
  const [selectedModelName, setSelectedModelName] = useState(searchParams.get('model_name') || 'all');
  const [selectedGender, setSelectedGender] = useState(searchParams.get('gender') || 'all');
  
  // Ref to hold the latest fetchData function for stable callback
  const fetchDataRef = useRef(null);
  const [weightRange, setWeightRange] = useState({ min: '', max: '' });
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();
  const { settings } = useSettings();

  // Get dynamic options from settings
  const metalCaratOptions = ['all', ...(settings?.metal_carats || ['22k', '18k', '14k', '9k'])];
  const modelCategoryOptions = ['all', ...(settings?.model_categories || ['Chain', 'Bangle', 'Bracelet', 'Ring', 'Necklace', 'Earring', 'Pendant', 'Anklet', 'Nose Pin', 'Mangalsutra'])];
  
  // Get unique model names from products
  const availableModelNames = ['all', ...new Set(products.filter(p => p.model_name).map(p => p.model_name))];

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedMetal !== 'all') params.metal_type = selectedMetal;

      const productsRes = await api.get('/products', { params: { ...params, limit: 100 } });
      const productsData = productsRes.data.products || productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
    }
  }, [selectedCategory, selectedMetal]);

  // Sync URL params with state only on initial load or URL change
  useEffect(() => {
    const category = searchParams.get('category');
    const modelName = searchParams.get('model_name');
    const modelCategory = searchParams.get('model_category');
    const gender = searchParams.get('gender');
    
    if (category) {
      setSelectedCategory(category.toLowerCase());
    } else {
      setSelectedCategory('all');
    }
    
    if (modelName) {
      setSelectedModelName(modelName);
    } else {
      setSelectedModelName('all');
    }
    
    if (modelCategory) {
      setSelectedModelCategory(modelCategory);
    } else {
      setSelectedModelCategory('all');
    }

    if (gender) {
      setSelectedGender(gender.toLowerCase());
    } else {
      setSelectedGender('all');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep fetchDataRef updated with latest fetchData
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Subscribe to stock changes from admin pages (runs once on mount)
  useEffect(() => {
    
    const unsubscribe = subscribeToStockChanges((data) => {
      // Use ref to always call the latest fetchData
      if (fetchDataRef.current) {
        fetchDataRef.current();
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array - only subscribe once

  const handleAddToCart = (product, price) => {
    addToCart(product, price);
    toast.success('Added to cart!');
  };

  const clearFilters = () => {
    setWeightRange({ min: '', max: '' });
    setPriceRange({ min: '', max: '' });
    setSelectedCarat('all');
    setSelectedModelCategory('all');
    setSelectedGender('all');
  };

  // Filter products by weight, price, carat, model category, and model name
  const filteredProducts = products.filter(product => {
    const price = calculatePrice(product, metalRates);
    if (!price) return false;

    // Weight filter
    if (weightRange.min && product.weight < parseFloat(weightRange.min)) return false;
    if (weightRange.max && product.weight > parseFloat(weightRange.max)) return false;

    // Price filter
    if (priceRange.min && parseFloat(price.totalPrice) < parseFloat(priceRange.min)) return false;
    if (priceRange.max && parseFloat(price.totalPrice) > parseFloat(priceRange.max)) return false;

    // Metal Carat filter
    if (selectedCarat !== 'all' && product.metal_carat !== selectedCarat) return false;

    // Model Category filter
    if (selectedModelCategory !== 'all' && product.model_category !== selectedModelCategory) return false;

    // Model Name filter
    if (selectedModelName !== 'all' && product.model_name !== selectedModelName) return false;

    // Gender filter - product.gender is an array, match if it includes the selected gender
    if (selectedGender !== 'all') {
      const productGenders = product.gender || [];
      if (!productGenders.some(g => g.toLowerCase() === selectedGender.toLowerCase())) return false;
    }

    return true;
  });

  const categories = ['all', 'gold', 'silver', 'diamond', 'platinum'];
  const metalTypes = ['all', 'Gold', 'Silver', 'Diamond', 'Platinum'];

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="products-page">
      {/* SEO Meta Tags */}
      <ProductsSEO category={selectedCategory !== 'all' ? selectedCategory : null} />
      
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 xl:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a3a5c] mb-2 md:mb-4 tracking-tight" data-testid="products-title">
            Our Collection
          </h1>
          <p className="text-gray-600 text-base md:text-lg">Discover timeless pieces that celebrate your unique style</p>
        </motion.div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Filters</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center space-x-2 text-[#1a3a5c] border border-gray-300 px-4 py-2 rounded-lg"
              data-testid="toggle-filters-button"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="text-sm">Filters</span>
            </button>
          </div>

          <div className={`${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c] bg-white"
                  data-testid="filter-category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Metal Type</label>
                <select
                  value={selectedMetal}
                  onChange={(e) => setSelectedMetal(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c] bg-white"
                  data-testid="filter-metal-select"
                >
                  {metalTypes.map((metal) => (
                    <option key={metal} value={metal}>
                      {metal === 'all' ? 'All' : metal}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[100px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Metal Carat</label>
                <select
                  value={selectedCarat}
                  onChange={(e) => setSelectedCarat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C9A967] bg-white"
                  data-testid="filter-carat-select"
                >
                  {metalCaratOptions.map((carat) => (
                    <option key={carat} value={carat}>
                      {carat === 'all' ? 'All' : carat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[130px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Model Category</label>
                <select
                  value={selectedModelCategory}
                  onChange={(e) => { setSelectedModelCategory(e.target.value); setSelectedModelName('all'); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c] bg-white"
                  data-testid="filter-model-select"
                >
                  {modelCategoryOptions.map((model) => (
                    <option key={model} value={model}>
                      {model === 'all' ? 'All' : model}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[130px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Model Name</label>
                <select
                  value={selectedModelName}
                  onChange={(e) => setSelectedModelName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c] bg-white"
                  data-testid="filter-model-name-select"
                >
                  {availableModelNames.map((name) => (
                    <option key={name} value={name}>
                      {name === 'all' ? 'All' : name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Weight (grams)</label>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Min"
                    value={weightRange.min}
                    onChange={(e) => setWeightRange({ ...weightRange, min: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c]"
                    data-testid="weight-min-input"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Max"
                    value={weightRange.max}
                    onChange={(e) => setWeightRange({ ...weightRange, max: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c]"
                    data-testid="weight-max-input"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Price (₹)</label>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c]"
                    data-testid="price-min-input"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#1a3a5c]"
                    data-testid="price-max-input"
                  />
                </div>
              </div>

              {(weightRange.min || weightRange.max || priceRange.min || priceRange.max || selectedCarat !== 'all' || selectedModelCategory !== 'all' || selectedCategory !== 'all' || selectedMetal !== 'all') && (
                <button
                  onClick={() => {
                    clearFilters();
                    setSelectedCategory('all');
                    setSelectedMetal('all');
                  }}
                  className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-2 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  data-testid="clear-filters-button"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-24" data-testid="no-products">
            <p className="text-gray-600 text-lg">No products found matching your filters.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-6">
              Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-5">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  price={calculatePrice(product, metalRates)}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}